// The editor's draft: built from a stored row (or empty), turned back into
// policyJson merged over what was stored (spec 7.1), and validated. Pure, so
// the round trip is unit-tested with each adapter. See adapterContract.js for
// the draft's shape.
import {
  conditionProblems,
  conditionRows,
  normaliseActions,
  normaliseConditions,
} from "../../../components/policyShell/conditionsModel";

// Error keys are "<section>.<field>", in page order: the basics card (name,
// Who, Where, Action), then the Advanced block.
export const SECTION_ORDER = ["basics", "who", "where", "action", "conditions", "except", "approval", "session"];
export const ADVANCED_SECTIONS = ["conditions", "except", "approval", "session"];

const EMPTY_APPROVAL = { required: false, approvers: [], validMinutes: 0, approverGroups: [], requireJustification: false, requireTicket: false };
const EMPTY_SESSION = { record: false, maxDurationMinutes: 0, idleTimeoutMinutes: 0, reauthIntervalMinutes: 0 };
const list = (v) => (Array.isArray(v) ? v.filter((x) => x !== null && x !== undefined && String(x).trim() !== "").map(String) : []);

// conditions -> { conditions without identity.exclude*, except: { users, groups } }.
export function splitExclusions(conditions) {
  if (!conditions || typeof conditions !== "object") return { conditions: undefined, except: { users: [], groups: [] } };
  const next = { ...conditions };
  const id = next.identity && typeof next.identity === "object" ? { ...next.identity } : null;
  const except = { users: list(id?.excludeUsers), groups: list(id?.excludeGroups) };
  if (id) {
    delete id.excludeUsers;
    delete id.excludeGroups;
    if (Object.keys(id).length) next.identity = id;
    else delete next.identity;
  }
  return { conditions: Object.keys(next).length ? next : undefined, except };
}

// The reverse: exclusions written into conditions.identity.
export function mergeExclusions(conditions, except) {
  const next = { ...(conditions || {}) };
  const users = list(except?.users);
  const groups = list(except?.groups);
  if (users.length || groups.length) {
    next.identity = { ...(next.identity || {}) };
    if (users.length) next.identity.excludeUsers = users;
    if (groups.length) next.identity.excludeGroups = groups;
  }
  return Object.keys(next).length ? next : undefined;
}

const whoWithLists = (adapter, who) => {
  const out = { everyone: !!who?.everyone };
  (adapter.who?.fields || []).forEach((f) => {
    out[f.key] = list(who?.[f.key]);
  });
  return out;
};

/** A new policy's draft. */
export function newDraft(adapter) {
  const base = adapter.emptyDraft ? adapter.emptyDraft() : {};
  return {
    policyName: "",
    who: whoWithLists(adapter, base.who),
    except: { users: [], groups: [] },
    conditions: undefined,
    action: base.action || "mfa_required",
    fallback: false,
    approval: { ...EMPTY_APPROVAL },
    session: { ...EMPTY_SESSION },
    product: base.product || {},
  };
}

/** A stored row's draft. duplicate: name it "Copy of …". */
export function draftFromRow(adapter, row, { duplicate = false } = {}) {
  const pj = row?.policyJson || {};
  const fromAdapter = adapter.fromPolicy(row) || {};
  const source = fromAdapter.conditions !== undefined ? fromAdapter.conditions : pj.conditions;
  const { conditions, except } = splitExclusions(source);
  const ap = pj.actions?.approval || {};
  const se = pj.actions?.session || {};
  const name = row?.policyName || pj.policyName || "";
  return {
    policyName: duplicate ? `Copy of ${name}` : name,
    who: whoWithLists(adapter, fromAdapter.who),
    except,
    conditions,
    action: adapter.actionOf(pj),
    fallback: !!pj.fallback,
    approval: {
      required: !!ap.required,
      approvers: list(ap.approvers),
      validMinutes: Number(ap.validMinutes) || 0,
      approverGroups: list(ap.approverGroups),
      requireJustification: !!ap.requireJustification,
      requireTicket: !!ap.requireTicket,
    },
    session: {
      record: !!se.record,
      maxDurationMinutes: Number(se.maxDurationMinutes) || 0,
      idleTimeoutMinutes: Number(se.idleTimeoutMinutes) || 0,
      reauthIntervalMinutes: Number(se.reauthIntervalMinutes) || 0,
    },
    product: fromAdapter.product || {},
  };
}

// Cleared on an edit: the server merges top-level keys, so a key left out
// keeps its stored value; null (or false) is what clears it.
const setOrClear = (obj, key, value, isEdit, cleared = null) => {
  if (value !== undefined) obj[key] = value;
  else if (isEdit) obj[key] = cleared;
  else delete obj[key];
};

/**
 * The policyJson to send: the adapter's product keys merged over stored, then
 * the shell's keys. stored = {} for a new policy (or the source of a duplicate).
 *
 * isEdit (spec 6): the update API merges policyJson onto the stored policy,
 * so clearing must be explicit: no conditions -> "conditions": null; fallback
 * off -> "fallback": false; SSH approval and session removed -> "actions": null.
 * Products without approval/session don't send "actions" on an edit (the
 * stored value is kept as it is).
 */
export function policyJsonFromDraft(adapter, draft, stored = {}, { isEdit = false } = {}) {
  const base = adapter.toPolicyJson(draft, stored || {}) || {};
  const out = { ...base, policyFlow: draft.action };
  if (Object.prototype.hasOwnProperty.call(out, "policyName")) out.policyName = draft.policyName.trim();
  setOrClear(out, "conditions", normaliseConditions(mergeExclusions(draft.conditions, draft.except), { keepUnknown: true }), isEdit);
  if (adapter.features?.fallback) out.fallback = !!draft.fallback;

  const { approval: hasApproval, session: hasSession } = adapter.features || {};
  if (hasApproval || hasSession) {
    const storedActions = stored?.actions && typeof stored.actions === "object" ? stored.actions : {};
    const actions = { ...storedActions };
    if (hasApproval) {
      const a = draft.approval || EMPTY_APPROVAL;
      actions.approval = a.required
        ? {
            ...(storedActions.approval || {}),
            required: true,
            approvers: a.approvers,
            validMinutes: a.validMinutes,
            approverGroups: a.approverGroups,
            requireJustification: a.requireJustification,
            requireTicket: a.requireTicket,
          }
        : undefined;
    }
    if (hasSession) actions.session = { ...(storedActions.session || {}), ...(draft.session || EMPTY_SESSION) };
    // normaliseActions keeps approval and session only; keep any other
    // action a newer console stored.
    const others = Object.fromEntries(Object.entries(actions).filter(([k, v]) => k !== "approval" && k !== "session" && v !== undefined));
    const norm = normaliseActions(actions, { policyFlow: draft.action }) || {};
    const merged = { ...others, ...norm };
    setOrClear(out, "actions", Object.keys(merged).length ? merged : undefined, isEdit);
  } else if (isEdit) {
    delete out.actions;
  }
  return out;
}

/** Is anything in the Advanced block set (spec 3: it opens on edit when so)? */
export function advancedState(adapter, draft) {
  const f = adapter.features || {};
  const conditionCount = conditionRows(draft.conditions, { product: adapter.key }).length;
  // Except is counted where the product offers it (not Apps, whose policy names one user).
  const hasExcept = adapter.who?.except !== false && (adapter.who?.fields || []).some((x) => x.except);
  const exceptCount = hasExcept ? (draft.except?.users || []).length + (draft.except?.groups || []).length : 0;
  const fallback = !!(f.fallback && draft.fallback);
  const approval = !!(f.approval && draft.action !== "block" && draft.approval?.required);
  const s = draft.session || {};
  const session = !!(f.session && (s.record || Number(s.maxDurationMinutes) || Number(s.idleTimeoutMinutes) || Number(s.reauthIntervalMinutes)));
  return { conditionCount, exceptCount, fallback, approval, session, any: !!(conditionCount || exceptCount || fallback || approval || session) };
}

const nounOf = (f) => f.noun || (/^[A-Z]+s$/.test(f.label) ? f.label.slice(0, -1) : f.label.replace(/s$/, "").toLowerCase());
const orList = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;

/** "Pick at least one user, group or OU, or choose Everyone." */
export function whoEmptyMessage(adapter) {
  const nouns = (adapter.who?.fields || []).map(nounOf);
  return `Pick at least one ${orList(nouns)}${adapter.who?.everyone ? ", or choose Everyone" : ""}.`;
}

const nonNeg = (v) => Number(v) >= 0;

/** Every problem in the draft: { "<section>.<field>": "Plain sentence." }. */
export function validateDraft(adapter, draft, { data } = {}) {
  const errors = {};
  if (!String(draft.policyName || "").trim()) errors["basics.policyName"] = "Give the policy a name.";

  const everyone = adapter.who?.everyone && draft.who?.everyone;
  const picked = (adapter.who?.fields || []).some((f) => (draft.who?.[f.key] || []).length);
  if (!everyone && !picked) errors["who.people"] = whoEmptyMessage(adapter);

  const cond = conditionProblems(draft.conditions, { product: adapter.key });
  Object.keys(cond).forEach((k) => {
    errors[`conditions.${k}`] = cond[k];
  });

  if (adapter.features?.approval && draft.action !== "block" && draft.approval?.required) {
    const a = draft.approval;
    if (!(a.approvers || []).length && !(a.approverGroups || []).length) errors["approval.approvers"] = "Name at least one approver.";
    if (!nonNeg(a.validMinutes)) errors["approval.validMinutes"] = "Enter 0 or more minutes.";
  }
  if (adapter.features?.session) {
    const s = draft.session || {};
    ["maxDurationMinutes", "idleTimeoutMinutes", "reauthIntervalMinutes"].forEach((k) => {
      if (!nonNeg(s[k])) errors[`session.${k}`] = "Enter 0 or more minutes.";
    });
    const max = Number(s.maxDurationMinutes) || 0;
    const idle = Number(s.idleTimeoutMinutes) || 0;
    if (!errors["session.idleTimeoutMinutes"] && max > 0 && idle > max) {
      errors["session.idleTimeoutMinutes"] = "Idle timeout must be shorter than the maximum length.";
    }
  }

  const own = (adapter.validate && adapter.validate(draft, data)) || {};
  Object.keys(own).forEach((k) => {
    if (own[k]) errors[k] = own[k];
  });
  return errors;
}

export const sectionOf = (key) => String(key).split(".")[0];
export const isAdvancedKey = (key) => ADVANCED_SECTIONS.includes(sectionOf(key));

/** Section keys with at least one error, in page order. */
export function sectionsWithErrors(errors) {
  const set = new Set(Object.keys(errors || {}).map(sectionOf));
  return SECTION_ORDER.filter((s) => set.has(s));
}

/** The error key to scroll to first: page order, then insertion order. */
export function firstErrorKey(errors) {
  const keys = Object.keys(errors || {});
  for (const s of SECTION_ORDER) {
    const k = keys.find((x) => sectionOf(x) === s);
    if (k) return k;
  }
  return keys[0] || null;
}

/** DOM id for an error key's field ("conditions.time" -> "pf-conditions-time"). */
export const fieldDomId = (key) => `pf-${String(key).replace(/[^a-zA-Z0-9]+/g, "-")}`;
